import { initDatabase, isDatabaseInitialized, getCurrentSessionId } from './database'
// Schema imports removed - no longer used with current implementation
// UUID imports removed - no longer used
import { sqliteWorkerManager, localStoragePersistence } from './worker-manager'

export interface GeometricProperties {
  // Sphere properties
  sphereSegments?: number
  sphereRings?: number
  
  // Cylinder properties
  cylinderRadialSegments?: number
  cylinderHeightSegments?: number
  
  // Cone properties
  coneRadialSegments?: number
  coneHeightSegments?: number
  
  // Cube properties
  cubeWidthSegments?: number
  cubeHeightSegments?: number
  cubeDepthSegments?: number
  
  // Plane properties
  planeWidthSegments?: number
  planeHeightSegments?: number
}

export interface SceneObject {
  id: string
  name?: string
  type: 'cube' | 'cylinder' | 'sphere' | 'plane' | 'cone'
  position: [number, number, number]
  rotation: [number, number, number]
  scale: [number, number, number]
  selected: boolean
  parentId?: string | null
  children?: string[]
  order: number // For sorting within the same parent
  properties?: GeometricProperties // Geometric properties for customization
}

export interface HistoryState {
  id: string
  objects: SceneObject[]
  timestamp: string
  action: string
}

// Use a default scene identifier
const DEFAULT_SCENE_ID = 'main-scene'

export class DatabaseService {
  private db: any
  private initialized: boolean = false
  private initializing: boolean = false
  private useWorker: boolean = false
  private useLocalStorage: boolean = false

  async init() {
    if (this.initialized) return
    if (this.initializing) {
      // Wait for ongoing initialization
      while (this.initializing) {
        await new Promise(resolve => setTimeout(resolve, 10))
      }
      return
    }
    
    this.initializing = true
    try {
      // For now, prioritize localStorage fallback for immediate testing
      console.log('[DatabaseService] Trying localStorage persistence first...')
      
      if (localStoragePersistence.isAvailable()) {
        this.useLocalStorage = true
        this.initialized = true
        console.log(`✅ LocalStorage persistence initialized for session: ${getCurrentSessionId()}`)
      } else {
        console.warn('LocalStorage not available, trying worker-based OPFS...')
        
        // Try worker-based OPFS
        const workerResult = await sqliteWorkerManager.init()
        if (workerResult.success) {
          this.useWorker = true
          this.initialized = true
          console.log(`✅ Worker-based SQLite initialized for session: ${getCurrentSessionId()}`)
        } else {
          console.warn('Worker-based SQLite failed, using in-memory fallback')
          
          // Final fallback to original in-memory database
          this.db = await initDatabase()
          this.initialized = true
          console.log(`✅ In-memory database initialized for session: ${getCurrentSessionId()}`)
        }
      }
    } catch (error) {
      console.error('Failed to initialize DatabaseService:', error)
      // Don't throw - allow the app to continue with in-memory state
      this.initialized = false
    } finally {
      this.initializing = false
    }
  }

  // Save current scene state
  async saveSceneState(objects: SceneObject[], action: string): Promise<void> {
    if (!this.initialized) {
      await this.init()
      if (!this.initialized) {
        console.warn('Database not available, skipping save')
        return
      }
    }

    try {
      if (this.useWorker) {
        await sqliteWorkerManager.saveSceneState(objects, action)
        return
      } else if (this.useLocalStorage) {
        await localStoragePersistence.saveSceneState(objects, action)
        return
      }
    } catch (error) {
      console.error('Failed to save with preferred method, falling back:', error)
    }
    
    if (!this.db) return

    try {
      const now = new Date().toISOString()
      const sceneId = DEFAULT_SCENE_ID

      // Create or update scene metadata FIRST to satisfy foreign key constraint
      this.db.exec({
        sql: `INSERT OR REPLACE INTO geometric_models 
              (id, name, description, created_at, updated_at)
              VALUES (?, ?, ?, 
                COALESCE((SELECT created_at FROM geometric_models WHERE id = ?), ?), 
                ?)`,
        bind: [sceneId, 'Main Scene', `Scene with ${objects.length} objects`, sceneId, now, now]
      })

      // Clear existing primitives for this scene
      this.db.exec({
        sql: 'DELETE FROM geometric_primitives WHERE model_id = ?',
        bind: [sceneId]
      })

      // Sort objects to save parents before children to avoid FK constraint violations
      const sortObjectsForSaving = (objects: SceneObject[]): SceneObject[] => {
        const objectMap = new Map(objects.map(obj => [obj.id, obj]))
        const visited = new Set<string>()
        const result: SceneObject[] = []

        const visit = (obj: SceneObject) => {
          if (visited.has(obj.id)) return
          visited.add(obj.id)

          // If this object has a parent, visit the parent first
          if (obj.parentId && objectMap.has(obj.parentId)) {
            visit(objectMap.get(obj.parentId)!)
          }

          result.push(obj)
        }

        objects.forEach(obj => visit(obj))
        return result
      }

      const sortedObjects = sortObjectsForSaving(objects)

      console.log('Saving objects in order:', sortedObjects.map(obj => ({
        id: obj.id,
        name: obj.name,
        parentId: obj.parentId,
        order: obj.order
      })))

      // Insert current objects in dependency order
      for (const obj of sortedObjects) {
        const properties = JSON.stringify({
          selected: obj.selected,
          geometric: obj.properties || {}
        })

        // Validate parent_id exists if not null
        let validParentId = obj.parentId
        if (validParentId && !objects.find(o => o.id === validParentId)) {
          console.warn(`Invalid parent_id ${validParentId} for object ${obj.id}, setting to null`)
          validParentId = null
        }

        this.db.exec({
          sql: `INSERT INTO geometric_primitives 
                (id, model_id, name, type, position_x, position_y, position_z, 
                 rotation_x, rotation_y, rotation_z, scale_x, scale_y, scale_z, 
                 parent_id, order_index, properties, created_at, updated_at)
                VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          bind: [
            obj.id, sceneId, obj.name || obj.id, obj.type,
          obj.position[0], obj.position[1], obj.position[2],
          obj.rotation[0], obj.rotation[1], obj.rotation[2],
          obj.scale[0], obj.scale[1], obj.scale[2],
            validParentId, obj.order || 0,
          properties, now, now
          ]
        })
      }

      console.log(`Saved scene state: ${action} (${objects.length} objects)`)
    } catch (error) {
      console.error('Failed to save scene state:', error)
    }
  }

  // Load scene state
  async loadSceneState(): Promise<SceneObject[]> {
    if (!this.initialized) {
      await this.init()
      if (!this.initialized) {
        console.warn('Database not available, returning empty scene')
        return []
      }
    }

    try {
      if (this.useWorker) {
        return await sqliteWorkerManager.loadSceneState()
      } else if (this.useLocalStorage) {
        return await localStoragePersistence.loadSceneState()
      }
    } catch (error) {
      console.error('Failed to load with preferred method, falling back:', error)
    }
    
    if (!this.db) return []

    try {
      const sceneId = DEFAULT_SCENE_ID
      
      const primitives = this.db.exec({
        sql: `SELECT * FROM geometric_primitives 
        WHERE model_id = ? 
              ORDER BY created_at`,
        bind: [sceneId],
        returnValue: 'resultRows'
      })

      if (!primitives || primitives.length === 0) {
        console.log('No saved scene found in database')
        return []
      }

      const objects: SceneObject[] = primitives.map((row: any[]) => {
        // Note: resultRows returns arrays, not objects
        // The column order matches the SELECT * order
        const [id, , name, type, posX, posY, posZ, rotX, rotY, rotZ, 
               scaleX, scaleY, scaleZ, parentId, orderIndex, properties] = row
        
        const parsedProperties = properties ? JSON.parse(properties) : {}
        
        return {
          id,
          name: name || id,
          type,
          position: [posX, posY, posZ] as [number, number, number],
          rotation: [rotX, rotY, rotZ] as [number, number, number],
          scale: [scaleX, scaleY, scaleZ] as [number, number, number],
          selected: parsedProperties.selected || false,
          parentId: parentId || null,
          children: [] as string[],
          order: orderIndex != null ? orderIndex : 0,
          properties: parsedProperties.geometric || {}
        }
      })

      // Build parent-child relationships
      const objectMap = new Map<string, SceneObject>(objects.map(obj => [obj.id, obj]))
      
      for (const obj of objects) {
        if (obj.parentId && objectMap.has(obj.parentId)) {
          const parent = objectMap.get(obj.parentId)!
          if (!parent.children!.includes(obj.id)) {
            parent.children!.push(obj.id)
          }
        }
      }

      // Sort children by order
      for (const obj of objects) {
        obj.children!.sort((a: string, b: string) => {
          const childA = objectMap.get(a)
          const childB = objectMap.get(b)
          return (childA?.order || 0) - (childB?.order || 0)
        })
      }

      console.log(`Loaded ${objects.length} objects from database`)
      return objects
    } catch (error) {
      console.error('Failed to load scene state:', error)
      return []
    }
  }

  // Get scene metadata
  async getSceneMetadata(): Promise<{ lastUpdated: string, objectCount: number } | null> {
    if (!this.initialized) {
      await this.init()
      if (!this.initialized) return null
    }
    
    if (!this.db) return null

    try {
      const sceneId = DEFAULT_SCENE_ID
      
      const result = this.db.exec({
        sql: `SELECT updated_at, 
                     (SELECT COUNT(*) FROM geometric_primitives WHERE model_id = ?) as object_count
              FROM geometric_models 
              WHERE id = ?`,
        bind: [sceneId, sceneId],
        returnValue: 'resultRows'
      })

      if (result && result.length > 0) {
        const [updatedAt, objectCount] = result[0]
        return {
          lastUpdated: updatedAt,
          objectCount: objectCount
        }
      }
      
      return null
    } catch (error) {
      console.error('Failed to get scene metadata:', error)
      return null
    }
  }

  // Check if database is available
  isAvailable(): boolean {
    return this.initialized && (this.useWorker || this.useLocalStorage || isDatabaseInitialized())
  }

  // Get current session info
  getSessionInfo(): { sessionId: string, isAvailable: boolean, isPersistent: boolean } {
    let isPersistent = false
    if (this.useWorker) {
      isPersistent = true // Worker-based OPFS is persistent
    } else if (this.useLocalStorage) {
      isPersistent = false // localStorage is less persistent
    } else if (this.db && isDatabaseInitialized()) {
      isPersistent = 'opfs' in (global as any).sqlite3
    }
    
    return {
      sessionId: getCurrentSessionId(),
      isAvailable: this.isAvailable(),
      isPersistent
    }
  }
}

// Create a singleton instance
const dbServiceInstance = new DatabaseService()

// Export the instance
export const dbService = dbServiceInstance 